/**
 * MasterFormulaSliders — group cards + per-layer auto-balanced weights
 * For Media Devoted | Created By Ciro Autiero
 *
 * Each group card shows one weight stepper per layer it applies to. Weights
 * are auto-balanced PER LAYER, so every layer (paths/landers/offers) always
 * sums to exactly 100% across the groups that score it — a landers-only group
 * never dilutes the paths or offers split.
 */

import { FC, ReactNode, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { ALL_LAYERS } from '@trafficpulse/shared';
import type { FlowLayer, FormulaConfig, FormulaGroup } from '@trafficpulse/shared';

import { Button } from '@/components/ui/layout/Button';

import FormulaGroupCard from './FormulaGroupCard';
import LayerTotalsCaption from './LayerTotalsCaption';
import { useOptionalFlowOptimizerLivePreview } from '../../context/FlowOptimizerLivePreviewContext';
import { UncoveredPathsWarning, findUncoveredPaths } from '../../ignore-paths';
import {
  buildPerLayerWeights,
  flattenPerLayerWeights,
  pickGroupLayerWeights,
  reconcilePerLayerWeights,
} from './per-layer-weights.utils';
import { usePerLayerWeights, type PerLayerWeights } from '../../hooks';
import { FormulaClipboardBar } from '../../clipboard';

export interface MasterFormulaSlidersProps {
  formula: FormulaConfig;
  onChange: (next: FormulaConfig) => void;
  /** Library set controls — rendered inside the clipboard bar. */
  libraryActions?: ReactNode;
}

const MasterFormulaSliders: FC<MasterFormulaSlidersProps> = ({
  formula,
  onChange,
  libraryActions,
}) => {
  const preview = useOptionalFlowOptimizerLivePreview();
  // Which paths no formula scores — recomputed as the draft changes, because
  // the preview refetches on every debounced edit.
  const uncovered = useMemo(
    () => findUncoveredPaths(formula, preview?.result?.items),
    [formula, preview?.result],
  );
  const initial = useMemo(() => buildPerLayerWeights(formula), [formula]);
  const sliders = usePerLayerWeights(initial);

  const commit = (groups: FormulaGroup[], perLayer: PerLayerWeights) =>
    onChange({
      ...formula,
      groups,
      groupWeights: flattenPerLayerWeights(perLayer, groups),
    });

  const setGroup = (id: string, next: FormulaGroup) =>
    onChange({
      ...formula,
      groups: formula.groups.map((g) => (g.id === id ? next : g)),
    });

  const removeGroup = (id: string) => {
    const groups = formula.groups.filter((g) => g.id !== id);
    commit(groups, reconcilePerLayerWeights(sliders.values, groups));
  };

  const addGroup = () => {
    const id = `g_${Date.now()}`;
    const groups = [...formula.groups, defaultGroup(id, formula.groups.length + 1)];
    commit(groups, reconcilePerLayerWeights(sliders.values, groups));
  };

  const setAppliesTo = (id: string, next: ReadonlyArray<FlowLayer>) => {
    const appliesTo = next.length === ALL_LAYERS.length ? undefined : next;
    const groups = formula.groups.map((g) =>
      g.id === id ? { ...g, appliesTo } : g,
    );
    commit(groups, reconcilePerLayerWeights(sliders.values, groups));
  };

  // Manual: write only this group's weight for this layer; siblings stay put.
  // The save gate (validateFormulaWeights) blocks until each layer totals 100.
  const setWeight = (id: string, layer: FlowLayer, next: number) => {
    const clamped = Math.max(0, Math.min(100, Number.isFinite(next) ? next : 0));
    const nextPerLayer: PerLayerWeights = {
      ...sliders.values,
      [layer]: { ...sliders.values[layer], [id]: clamped },
    };
    sliders.setValue(layer, id, clamped);
    commit(formula.groups, nextPerLayer);
  };

  return (
    <div className="space-y-3">
      <FormulaClipboardBar
        formula={formula}
        onChange={onChange}
        libraryActions={libraryActions}
      />
      {formula.groups.length === 0 ? (
        <EmptyState onAdd={addGroup} />
      ) : (
        <>
          <div className="space-y-3">
            {formula.groups.map((group) => (
              <FormulaGroupCard
                key={group.id}
                group={group}
                weights={pickGroupLayerWeights(sliders.values, group)}
                onChange={(next) => setGroup(group.id, next)}
                onWeightChange={(layer, next) => setWeight(group.id, layer, next)}
                onAppliesToChange={(next) => setAppliesTo(group.id, next)}
                onRemove={() => removeGroup(group.id)}
              />
            ))}
          </div>
          <LayerTotalsCaption weights={sliders.values} groups={formula.groups} />
          <UncoveredPathsWarning paths={uncovered} />
          <div className="border-t border-border/60 pt-3">
            <AddButton onAdd={addGroup} />
          </div>
        </>
      )}
    </div>
  );
};

const AddButton: FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <Button
    variant="outline"
    size="sm"
    type="button"
    onClick={onAdd}
    className="h-8 px-3 text-xs"
  >
    <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
    Add formula group
  </Button>
);

const EmptyState: FC<{ onAdd: () => void }> = ({ onAdd }) => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border bg-muted/20 px-4 py-6 text-center">
    <p className="text-xs text-muted-foreground">
      No formula groups yet. Add one to define how items are scored.
    </p>
    <AddButton onAdd={onAdd} />
  </div>
);

function defaultGroup(id: string, index: number): FormulaGroup {
  return {
    id,
    name: `Group ${index}`,
    dateRange: 'last-7-days',
    mode: 'sliders',
    sliders: { metrics: [] },
    expression: '0',
    // Undefined => applies to all layers; same as ['paths','landers','offers']
    appliesTo: undefined,
  };
}

export default MasterFormulaSliders;
